export type RoomStatus = 'WAITING' | 'STARTING' | 'PLAYING' | 'COMPLETED' | 'CLOSED';

export type GameStage = 'DINING_TYPE' | 'MAIN_CATEGORY' | 'SUB_CATEGORY' | 'MENU' | 'RESULT';

/** 선택이 이루어지는 단계 (순서대로) */
export const SELECTION_STAGES = ['DINING_TYPE', 'MAIN_CATEGORY', 'SUB_CATEGORY', 'MENU'] as const;
export type SelectionStage = (typeof SELECTION_STAGES)[number];

export const STAGE_LABELS: Record<SelectionStage, { title: string; progress: string; mine: string; mineSub: string; others: string }> = {
  DINING_TYPE: {
    title: '식사 방식',
    progress: '배달할지 나갈지 정하는 중',
    mine: '지금은 당신의 차례예요!',
    mineSub: '오늘은 어떻게 먹을까요?',
    others: '식사 방식을 정하고 있어요',
  },
  MAIN_CATEGORY: {
    title: '음식 종류',
    progress: '음식 종류를 정하는 중',
    mine: '음식 종류를 골라주세요!',
    mineSub: '오늘 당기는 음식은?',
    others: '음식 종류를 정하고 있어요',
  },
  SUB_CATEGORY: {
    title: '세부 종류',
    progress: '세부 종류를 정하는 중',
    mine: '조금 더 구체적으로!',
    mineSub: '가게 종류에 따라 여기서 결정될 수도 있어요.',
    others: '가게 종류에 따라 여기서 결정될 수도 있어요',
  },
  MENU: {
    title: '최종 메뉴',
    progress: '오늘의 메뉴를 정하는 중',
    mine: '마지막 선택이에요!',
    mineSub: '오늘의 점심 메뉴를 골라주세요.',
    others: '오늘의 메뉴가 곧 결정돼요!',
  },
};

export const RANDOM_BUTTON_LABELS: Record<SelectionStage, string> = {
  DINING_TYPE: '🎲 랜덤으로 정하기',
  MAIN_CATEGORY: '🎲 아무거나!',
  SUB_CATEGORY: '🎲 랜덤 선택',
  MENU: '🎲 운에 맡기기',
};

export interface Participant {
  id: string;
  nickname: string;
  joinedAt: number;
  connected: boolean;
  /** false면 게임 도중 명시적으로 퇴장한 참여자 (이후 턴에서 제외) */
  active: boolean;
  isHost: boolean;
}

export interface SelectOption {
  id: string;
  name: string;
  emoji: string;
  description?: string;
}

export interface Selection {
  stage: SelectionStage;
  optionId: string;
  optionName: string;
  emoji: string;
  participantId: string;
  participantNickname: string;
  isRandom: boolean;
  /** 담당자 연결 끊김으로 서버가 자동 선택한 경우 */
  isAuto: boolean;
  selectedAt: number;
}

export interface GameResult {
  menuId: string;
  menuName: string;
  emoji: string;
  /** 식사 방식 → 대분류 → 중분류 (→ 메뉴) 순서의 경로 */
  path: Selection[];
  /**
   * SUB_CATEGORY: 중분류에서 가게가 정해져 3단계에서 종료 (메뉴는 각자 주문)
   * MENU: 4단계 세부 메뉴까지 결정
   */
  decidedAtStage: 'SUB_CATEGORY' | 'MENU';
  /** 3단계 종료 시 참고용 메뉴 목록 */
  suggestedMenus: SelectOption[];
  completedAt: number;
}

/**
 * 클라이언트에 전달되는 공개 방 상태.
 * IMPORTANT: 전체 턴 순서(turnOrder), 다음 담당자, 비밀번호 해시, 재접속 토큰은 절대 포함하지 않습니다.
 */
export interface PublicRoomState {
  id: string;
  name: string;
  maxParticipants: number;
  hasPassword: boolean;
  hostId: string;
  participants: Participant[];
  status: RoomStatus;
  stage: GameStage;
  /** 현재 선택 중인 참여자 (PLAYING 상태에서만 값이 있음) */
  currentParticipantId: string | null;
  currentParticipantNickname: string | null;
  /** 현재 담당자가 연결이 끊어진 경우, 자동 선택 예정 시각 */
  chooserDeadline: number | null;
  /** STARTING 상태에서 게임이 실제로 시작되는 시각 */
  startsAt: number | null;
  /** 현재 단계에서 선택 가능한 후보 */
  options: SelectOption[];
  /** 완료된 단계의 선택 기록 */
  selections: Selection[];
  result: GameResult | null;
  /** 같은 방에서 진행한 게임 횟수 (다시하기 포함) */
  round: number;
  version: number;
  createdAt: number;
  updatedAt: number;
  expiresAt: number;
}

export interface RoomInfo {
  id: string;
  name: string;
  currentCount: number;
  maxParticipants: number;
  hasPassword: boolean;
  status: RoomStatus;
}
